export type Accent = 'sage' | 'lilac' | 'terracotta' | 'gold'

export interface Volume {
  code: string
  name: string
  subtitle: string
  count: string
  intro: string
  focus: string[]
  bestFor?: string
  result: string
  accent: Accent
}

export const volumes: Volume[] = [
  {
    code: '01',
    name: 'Foundations',
    subtitle: 'Beginner & Gentle Yoga',
    count: '20 Ready-to-Teach Classes',
    intro:
      'The volume to open when you’re teaching newer students, gentle classes or groups that need simplicity, clarity and confidence.',
    focus: [
      'Yoga foundations',
      'Breath + movement',
      'Standing basics',
      'Gentle full-body movement',
      'Balance',
      'Hips & hamstrings',
      'Shoulders',
      'Spinal movement',
      'Accessible strength',
      'Morning practice',
      'Grounding',
      'Confidence',
      'Full-body beginner flows',
    ],
    bestFor: 'Beginners • New Students • Gentle Classes • Foundational Teaching',
    result:
      'You don’t have to wonder how to simplify your class. The structure is already there.',
    accent: 'sage',
  },
  {
    code: '02',
    name: 'Hatha',
    subtitle: 'Strength, Balance & Alignment',
    count: '20 Ready-to-Teach Classes',
    intro:
      'A complete collection of structured Hatha classes designed to give you variety without losing the steady, intentional character of the practice.',
    focus: [
      'Hatha foundations',
      'Standing sequences',
      'Strength',
      'Hip-focused classes',
      'Shoulders',
      'Core',
      'Balance',
      'Hamstrings',
      'Backbend foundations',
      'Twists',
      'Grounding',
      'Energizing classes',
      'Evening Hatha',
      'Complete Hatha flows',
      'Longer sessions',
    ],
    result:
      'You can keep your Hatha teaching structured and varied without rebuilding your sequence every week.',
    accent: 'terracotta',
  },
  {
    code: '03',
    name: 'Flow',
    subtitle: 'Slow Flow & Accessible Vinyasa',
    count: '20 Ready-to-Teach Classes',
    intro:
      'For those classes where movement needs to feel connected rather than assembled.',
    focus: [
      'Intro to Slow Flow',
      'Breath-led movement',
      'Accessible Vinyasa',
      'Standing flows',
      'Gentle full-body flows',
      'Warrior sequences',
      'Balance flows',
      'Hip-focused flows',
      'Strength flows',
      'Morning energy',
      'Mobility Flow',
      'Grounding Flow',
      'Evening Slow Flow',
      'Mixed-level Flow',
      'Complete Accessible Vinyasa',
      'Extended signature classes',
    ],
    result:
      'Transitions feel intentional. Pacing becomes easier. And the class feels like one complete experience rather than a list of poses.',
    accent: 'lilac',
  },
  {
    code: '04',
    name: 'Mobility',
    subtitle: 'Mobility & Functional Movement',
    count: '20 Ready-to-Teach Classes',
    intro: 'When students want to move better — not simply move more.',
    focus: [
      'Full-body mobility',
      'Hips',
      'Shoulders',
      'Spine',
      'Ankles & feet',
      'Wrists',
      'Hamstrings',
      'Hip rotation',
      'Rotation',
      'Side body',
      'Mobility + stability',
      'Squat patterns',
      'Lunge patterns',
      'Balance & control',
      'Desk-to-mat movement',
      'Morning mobility',
      'Evening mobility',
    ],
    result:
      'Instead of randomly adding mobility drills to class, you have complete sessions organized around a clear movement focus.',
    accent: 'gold',
  },
  {
    code: '05',
    name: 'Restore',
    subtitle: 'Restorative, Yin-Inspired & Breath-Based Classes',
    count: '20 Ready-to-Teach Classes',
    intro:
      'For the days when your students don’t need more intensity. They need somewhere to land.',
    focus: [
      'Restorative foundations',
      'Full-body rest',
      'Evening practices',
      'Lower-body release',
      'Upper-body release',
      'Yin-inspired classes',
      'Hip-focused stillness',
      'Back-body work',
      'Spinal movement',
      'Breath-focused classes',
      'Floor-only practices',
      'Low-energy yoga',
      'Quiet mindful movement',
      'End-of-day sessions',
      'Deep support',
      'Complete restorative sessions',
    ],
    result:
      'You have slower classes ready without having to improvise the pacing, structure or experience.',
    accent: 'lilac',
  },
  {
    code: '06',
    name: 'Adapt',
    subtitle: 'Chair, Mixed-Level & Real-World Teaching',
    count: '20 Ready-to-Teach Classes',
    intro:
      'This may become the volume you open when teaching doesn’t go according to plan. Because real classes aren’t always predictable.',
    focus: [
      'Chair Yoga',
      'Chair Mobility',
      'Chair + Standing',
      'Seated Flow',
      'Mixed-level classes',
      'Multiple options',
      'No-prop classes',
      'Small spaces',
      'Substitute teaching',
      'Low-energy groups',
      'Shorter classes',
      'Grounding',
      'Fresh-start themes',
      'Gratitude',
      'Confidence',
      'Adaptable full classes',
    ],
    result:
      'When the room changes, your entire plan doesn’t have to collapse.',
    accent: 'sage',
  },
]
